/**
 * ToastContext - Lightweight notification system
 *
 * Provides `addToast(message, type?, duration?)` for showing
 * auto-dismissing feedback messages. Stacks up to 3.
 *
 * @module @humanizer/studio/contexts/ToastContext
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
  action?: ToastAction;
  /** Set to true when fade-out begins */
  dismissing?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type?: ToastType, opts?: { duration?: number; action?: ToastAction }) => void;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const MAX_TOASTS = 3;

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: number) => {
    // Start fade-out animation
    setToasts(prev => prev.map(t => t.id === id ? { ...t, dismissing: true } : t));
    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
    // Clear auto-dismiss timer if any
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const addToast = useCallback((
    message: string,
    type: ToastType = 'info',
    opts?: { duration?: number; action?: ToastAction },
  ) => {
    const id = nextId.current++;
    const duration = opts?.duration ?? 3000;

    setToasts(prev => {
      const next = [...prev, { id, message, type, duration, action: opts?.action }];
      // Trim oldest if over limit
      if (next.length > MAX_TOASTS) {
        const oldest = next[0];
        const timer = timersRef.current.get(oldest.id);
        if (timer) {
          clearTimeout(timer);
          timersRef.current.delete(oldest.id);
        }
        return next.slice(next.length - MAX_TOASTS);
      }
      return next;
    });

    // Auto-dismiss
    if (duration > 0) {
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        dismissToast(id);
      }, duration);
      timersRef.current.set(id, timer);
    }
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}
